import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { GeoPolygon } from '@soc/shared';
import {
  useCreateOrganizationMutation,
  useDeleteOrganizationMutation,
  useGetOrganizationsQuery,
  useUpdateOrganizationMutation,
} from '../app/api';
import { apiErrorMessage } from '../lib/validation';
import { ErrorNote, Field, inputStyle, primaryButtonStyle } from '../features/admin/adminUi';
import {
  AdminFormShell,
  FormHeader,
  FormSection,
  FormSplit,
  RecordMeta,
  SegmentedControl,
  dangerBtnStyle,
  secondaryBtnStyle,
} from '../features/admin/formUi';
import { PolyMap } from '../features/admin/PolyMap';

// Cosmetic-only reference fields (rendered for fidelity, not persisted).
const STATUS = [
  { value: 'active', label: 'Active' },
  { value: 'provisioning', label: 'Provisioning' },
  { value: 'suspended', label: 'Suspended' },
];
const TIERS = [
  { value: 'standard', label: 'Standard' },
  { value: 'pro', label: 'Pro' },
  { value: 'gov', label: 'Gov' },
];

export default function OrgForm({ mode }: { mode: 'create' | 'edit' }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: orgs } = useGetOrganizationsQuery();
  const record = mode === 'edit' ? orgs?.items.find((o) => o.id === id) : undefined;

  const [createOrg, { isLoading: creating }] = useCreateOrganizationMutation();
  const [updateOrg, { isLoading: updating }] = useUpdateOrganizationMutation();
  const [deleteOrg] = useDeleteOrganizationMutation();

  // real, persisted fields
  const [name, setName] = useState('');
  const [maxArea, setMaxArea] = useState('');
  const [polygon, setPolygon] = useState<GeoPolygon | null>(null);
  // cosmetic
  const [status, setStatus] = useState('active');
  const [tier, setTier] = useState('pro');
  const [contactName, setContactName] = useState('');
  const [phone, setPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [towerQuota, setTowerQuota] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(mode === 'create');

  useEffect(() => {
    if (mode === 'edit' && record && !hydrated) {
      setName(record.name);
      setMaxArea(record.maxAreaKmSq != null ? String(record.maxAreaKmSq) : '');
      setPolygon(record.polygon ?? null);
      setHydrated(true);
    }
  }, [mode, record, hydrated]);

  const maxAreaNum = Number(maxArea);
  const saving = creating || updating;
  const canSave =
    name.trim().length > 0 && polygon != null && maxArea.trim() !== '' && maxAreaNum > 0 && !saving;

  const submit = async () => {
    setError(null);
    if (!canSave || !polygon) return;
    try {
      if (mode === 'create') {
        await createOrg({ name: name.trim(), polygon, maxAreaKmSq: maxAreaNum }).unwrap();
      } else if (id) {
        await updateOrg({ id, name: name.trim(), polygon, maxAreaKmSq: maxAreaNum }).unwrap();
      }
      navigate('/organizations');
    } catch (e) {
      setError(apiErrorMessage(e, 'Failed to save organization'));
    }
  };

  const remove = async () => {
    if (!id) return;
    if (!window.confirm('Delete this organization? Its projects and users will be orphaned.')) return;
    try {
      await deleteOrg(id).unwrap();
      navigate('/organizations');
    } catch (e) {
      setError(apiErrorMessage(e, 'Failed to delete organization'));
    }
  };

  const actions = (
    <>
      {mode === 'edit' && (
        <button type="button" style={dangerBtnStyle} onClick={remove}>
          Delete
        </button>
      )}
      <Link to="/organizations" style={secondaryBtnStyle}>
        Cancel
      </Link>
      <button type="button" style={primaryButtonStyle(canSave)} disabled={!canSave} onClick={submit}>
        {saving ? 'Saving…' : mode === 'create' ? 'Create organization' : 'Save changes'}
      </button>
    </>
  );

  const ready = mode === 'create' || hydrated;

  return (
    <AdminFormShell activeEntity="organizations" actions={actions}>
      <FormSplit
        form={
          <>
            <FormHeader
              eyebrow={mode === 'create' ? 'New organization' : 'Edit organization'}
              title={mode === 'create' ? 'Add an organization' : record?.name ?? 'Organization'}
              subtitle="An organization owns its projects and users, and a coverage boundary within which projects may be created."
              meta={mode === 'edit' && record ? <RecordMeta id={record.id} createdAt={record.createdAt} /> : undefined}
            />

            <FormSection title="Identity">
              <Field label="Organization name">
                <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Northwind Telecom" />
              </Field>
              <Field label="Status">
                <SegmentedControl options={STATUS} isActive={(v) => v === status} onClick={setStatus} />
              </Field>
            </FormSection>

            <FormSection title="Primary contact">
              <Field label="Contact name">
                <input style={inputStyle} value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Jordan Vance" />
              </Field>
              <Field label="Phone">
                <input className="mono" style={inputStyle} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 0100" />
              </Field>
              <Field label="Contact email">
                <input style={inputStyle} type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="ops@northwind.net" />
              </Field>
            </FormSection>

            <FormSection title="Plan & quota">
              <Field label="Plan tier">
                <SegmentedControl options={TIERS} isActive={(v) => v === tier} onClick={setTier} />
              </Field>
              <Field label="Monitored tower quota">
                <input className="mono" style={inputStyle} value={towerQuota} onChange={(e) => setTowerQuota(e.target.value)} placeholder="1500" />
              </Field>
              <Field label="Max coverage area (km²)" hint="The total project area allowed inside this organization.">
                <input
                  className="mono"
                  style={inputStyle}
                  type="number"
                  min={0}
                  value={maxArea}
                  onChange={(e) => setMaxArea(e.target.value)}
                  placeholder="50000"
                />
              </Field>
            </FormSection>

            {error && <ErrorNote>{error}</ErrorNote>}
          </>
        }
        panel={
          ready ? (
            <PolyMap
              initialPolygon={mode === 'edit' ? record?.polygon ?? null : null}
              onChange={setPolygon}
            />
          ) : (
            <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: 'var(--text-faint)' }}>
              Loading…
            </div>
          )
        }
      />
    </AdminFormShell>
  );
}
